import client from 'client'
import type { GetIssuesIssueGetQueryResponse, GetIssuesIssueGetQueryParams, GetIssuesIssueGet422 } from '../../types/GetIssuesIssueGet.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getIssuesIssueGetQueryKey = (params?: GetIssuesIssueGetQueryParams) => [{ url: '/issue' }, ...(params ? [params] : [])] as const

export type GetIssuesIssueGetQueryKey = ReturnType<typeof getIssuesIssueGetQueryKey>

/**
 * @summary Get Issues
 * {@link /issue}
 */
export async function getIssuesIssueGet(params?: GetIssuesIssueGetQueryParams, config: Partial<RequestConfig> & { client?: typeof client } = {}) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<GetIssuesIssueGetQueryResponse, ResponseErrorConfig<GetIssuesIssueGet422>, unknown>({
    method: 'GET',
    url: `/issue`,
    params,
    ...requestConfig,
  })
  return res
}

export function getIssuesIssueGetQueryOptions(params?: GetIssuesIssueGetQueryParams, config: Partial<RequestConfig> & { client?: typeof client } = {}) {
  const queryKey = getIssuesIssueGetQueryKey(params)
  return queryOptions<
    ResponseConfig<GetIssuesIssueGetQueryResponse>,
    ResponseErrorConfig<GetIssuesIssueGet422>,
    ResponseConfig<GetIssuesIssueGetQueryResponse>,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getIssuesIssueGet(params, config)
    },
  })
}

/**
 * @summary Get Issues
 * {@link /issue}
 */
export function useGetIssuesIssueGet<
  TData = ResponseConfig<GetIssuesIssueGetQueryResponse>,
  TQueryData = ResponseConfig<GetIssuesIssueGetQueryResponse>,
  TQueryKey extends QueryKey = GetIssuesIssueGetQueryKey,
>(
  params?: GetIssuesIssueGetQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<ResponseConfig<GetIssuesIssueGetQueryResponse>, ResponseErrorConfig<GetIssuesIssueGet422>, TData, TQueryData, TQueryKey>
    >
    client?: Partial<RequestConfig> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getIssuesIssueGetQueryKey(params)

  const query = useQuery({
    ...(getIssuesIssueGetQueryOptions(params, config) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetIssuesIssueGet422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}